import type { DecisionTemplateSubtypeKey } from '../models/decision-template';
import type { ISubmissionContextConfig } from '../models/submission-context';

export type ReviewStage = 'pre' | 'post';

export type RejectionOptionsConfig = Pick<
  ISubmissionContextConfig,
  'preRejectionOptions' | 'postRejectionOptions'
>;

const passedThroughPeerReview = (stateName: string) =>
  stateName === 'revision' ||
  stateName === 'revisionAssessment' ||
  stateName.startsWith('peerReview') ||
  stateName.startsWith('revisionPeerReview');

export function getReviewStage(workflowData: {
  currentState?: string;
  states?: Array<{ name: string }>;
}): ReviewStage {
  const names = [
    workflowData.currentState,
    ...(workflowData.states ?? []).map(state => state.name)
  ].filter((name): name is string => Boolean(name));

  return names.some(passedThroughPeerReview) ? 'post' : 'pre';
}

/** Union of pre and post rejection reason strings. */
export function allRejectionReasonOptions(
  config: RejectionOptionsConfig
): string[] {
  return [
    ...new Set([
      ...(config.preRejectionOptions ?? []),
      ...(config.postRejectionOptions ?? [])
    ])
  ];
}

/** Reasons shown in the reject modal for the current review stage. */
export function rejectionReasonsForReviewStage(
  config: RejectionOptionsConfig,
  stage: ReviewStage
): string[] {
  const forStage =
    stage === 'pre'
      ? (config.preRejectionOptions ?? [])
      : (config.postRejectionOptions ?? []);

  if (forStage.length > 0) {
    return forStage;
  }

  return allRejectionReasonOptions(config);
}

const WITHOUT_REVIEW_SUFFIX = ' (without review)';
const WITH_REVIEW_SUFFIX = ' (with review)';

/** Display label for admin UI (decision letter subtype dropdown). */
export function rejectionReasonDisplayLabel(
  reason: string,
  config: RejectionOptionsConfig
): string {
  if ((config.preRejectionOptions ?? []).includes(reason)) {
    return `${reason}${WITHOUT_REVIEW_SUFFIX}`;
  }

  if ((config.postRejectionOptions ?? []).includes(reason)) {
    return `${reason}${WITH_REVIEW_SUFFIX}`;
  }

  return reason;
}

export function rejectionDecisionTemplateSubtypes(
  config: RejectionOptionsConfig
): Array<{ label: string; type: string }> {
  return [
    ...(config.preRejectionOptions ?? []).map(reason => ({
      label: `${reason}${WITHOUT_REVIEW_SUFFIX}`,
      type: reason
    })),
    ...(config.postRejectionOptions ?? []).map(reason => ({
      label: `${reason}${WITH_REVIEW_SUFFIX}`,
      type: reason
    }))
  ];
}

/**
 * One email sub-event per rejection reason. Email templates are keyed by
 * `EditorialSubmissionRejected#${reason}`, which does not include review
 * stage, so a reason configured for both desk reject and post-review must
 * appear once. A pre-review reason is labeled "(without review)"; a reason
 * that exists only after review is labeled "(with review)".
 */
export function rejectionEmailEventSubtypes(
  config: RejectionOptionsConfig
): Array<{ label: string; type: string }> {
  const pre = new Set(config.preRejectionOptions ?? []);
  const post = new Set(config.postRejectionOptions ?? []);

  return [...new Set([...pre, ...post])].map(type => ({
    label: pre.has(type)
      ? `${type}${WITHOUT_REVIEW_SUFFIX}`
      : `${type}${WITH_REVIEW_SUFFIX}`,
    type
  }));
}

/** Subtype options for decision-letter templates (reject uses pre/post union). */
export function decisionTemplateSubtypeOptions(
  config: RejectionOptionsConfig &
    Partial<
      Pick<
        ISubmissionContextConfig,
        'revisionSeverityOptions' | 'editorialWithdrawalOptions'
      >
    >,
  subtypeKey: DecisionTemplateSubtypeKey
): string[] {
  if (subtypeKey === 'rejectionOptions') {
    return allRejectionReasonOptions(config);
  }

  return config[subtypeKey] ?? [];
}
